import {
    Injectable, inject
} from "@angular/core";
import {
    FileRequestSubject,
    FileResponse,
    RequestUtil
} from "@shopliftr/common-js/shared";
import { MimeType } from "@shopliftr/common-js/data";
import { Observable } from "rxjs";

import { IInternalApiFile } from "../../../../interfaces/internal-api-file";
import { InternalApiFileRequestService } from "../../request/internal-api/internal-api-file-request.service";

/**
 * API File Service for BYOD (Bring Your Own Data) import spreadsheet file operations.
 *
 * Handles file upload operations for BYOD import spreadsheets, delegating HTTP
 * communication to InternalApiFileRequestService. This service is scoped to the
 * upload operation only, as BYOD import spreadsheets are submitted for processing
 * and do not require list or delete operations from the client.
 *
 * @example
 * ```typescript
 * const fullPath = `${baseName}-${Date.now()}.${extension}`;
 * byodImportSpreadsheetApiFileService.upload$(fullPath, file, MimeType.MsExcelXml)
 *     .subscribe((response) => { ... });
 * ```
 *
 * @pattern API File Service / BYOD Import Spreadsheet File Upload
 * @providedIn root
 * @implements {IInternalApiFile}
 */
@Injectable({
    providedIn: "root"
})
export class ByodImportSpreadsheetApiFileService implements IInternalApiFile {

    readonly fileRequestSubject = FileRequestSubject.ByodImportSpreadsheet;

    /**
     * Handles HTTP communication with Internal API for file operations.
     */
    private readonly _internalApiFileRequestService = inject(InternalApiFileRequestService);

    /**
     * Uploads a BYOD import spreadsheet file to Internal API.
     *
     * The caller is responsible for assembling the full path (including any
     * timestamp or uniqueness suffix) before invoking this method.
     *
     * @param fullPath - Fully assembled file path including filename and extension
     * @param file - File blob to upload
     * @param fileType - MIME type of the file
     * @returns Observable that emits the file upload response
     */
    upload$(fullPath: string, file: Blob, fileType: MimeType): Observable<FileResponse> {

        const request = RequestUtil.createFileUploadRequest(FileRequestSubject.ByodImportSpreadsheet, fullPath, fileType);
        return this._internalApiFileRequestService.uploadFile$(request, file);
    }
}
